import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { DateTimePicker, LocaleEnum, getLanguageCodeByLocale } from '@systemeio/ui-frontend'
import type { Emoji } from 'emoji-mart'
import { useTranslation } from 'next-i18next'
import type { SubmitHandler, UseFormSetError } from 'react-hook-form'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { PrimaryButton } from 'shared/components/button'
import CheckBoxSwitcher from 'shared/components/checkbox-switcher'
import Modal from 'shared/components/modal'
import { COMMUNITY_TITLE_MAX_LENGTH, EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import PlusIcon from 'shared/icons/circle-plus-icon'
import { determineTimezone } from 'shared/utils/date-time/determine-timezone'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { hasEmbeddedVideoAttachmentUrlTooLong } from 'modules/attachments/utils/attachment-utils'
import { useCreatePost } from 'modules/community/api/use-create-post'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import { generatePathFromName } from 'modules/community/hooks/path-utils'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import type { PostInterface } from 'modules/community/types/post-interface'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'
import PostAttachments from './components/attachments'
import type { EditorRef } from './components/content'
import AddPostContentEditor from './components/content'
import AddPostInputField from './components/input-field'
import AddPostTopicSelect from './components/topic-select'
import type { CreatePostFormValues } from './types'
import { validateNotPastDate } from './validations'

const defaultValues: Omit<CreatePostFormValues, 'scheduledTo'> = {
  title: '',
  path: '',
  content: '',
}

type ErrorFields = Parameters<UseFormSetError<CreatePostFormValues>>[0]

const errorFields: ErrorFields[] = ['title', 'path', 'topic', 'content', 'root', 'scheduledTo']

interface AddPostProps {
  onAdd?: (post: PostInterface) => void
}

function AddPost({ onAdd }: AddPostProps): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()

  const [opened, setOpened] = useState(false)
  const [displaySchedulingOptions, setDisplaySchedulingOptions] = useState(false)
  const { data: community, isValidating } = useCommunityWithTopics()
  const { isUploading } = usePostAttachmentsContext()

  const createPostMutation = useCreatePost({
    communityPath: community?.path ?? '',
  })

  const formMethods = useForm<CreatePostFormValues>({ defaultValues })
  const canSchedulePosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const timezone = determineTimezone(user)
  const scheduledTo = formMethods.watch('scheduledTo')
  const scheduledToFormatted = useFormatDateTime(scheduledTo)
  const editorRef = useRef<EditorRef>(null)

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>): void {
    if (!e.target.value) {
      formMethods.setValue('path', generatePathFromName(formMethods.getValues('title')))
    }
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props): void =>
    editorRef.current?.insertEmoji(emoji.native)

  const onSubmit: SubmitHandler<CreatePostFormValues> = formData => {
    if (!community) return

    formMethods.clearErrors()
    const { title, path, content, topic, attachments, scheduledTo } = formData

    if (hasEmbeddedVideoAttachmentUrlTooLong(attachments)) {
      formMethods.setError('root', {
        message: t('post.attachments.embedded_video_link.error_too_long', {
          max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
        }),
      })
      return
    }

    createPostMutation.mutate(
      {
        title,
        path,
        content,
        topic,
        scheduledTo,
        attachments: escapeAttachmentLocalFields(attachments),
      },
      {
        onSuccess: data => {
          if (scheduledTo) {
            toast.success(t('community.add_post.scheduled_message'))
          } else if (data.status !== PostStatusEnum.Approved) {
            toast.success(t('community.add_post.sent_to_approval_message'))
          }

          onAdd?.(data)
          setOpened(false)
          formMethods.reset(defaultValues)
          editorRef.current?.clear()
          setDisplaySchedulingOptions(false)
        },
        onError: error => {
          if (error instanceof BadRequest) {
            errorFields.forEach(name => {
              const fieldError = error.errors?.fields?.[name]
              if (fieldError) {
                formMethods.setError(name, { message: fieldError.join('\n') })
              }

              if (name === 'root' && error.errors?.common) {
                formMethods.setError(name, { message: error.errors.common.join('\n') })
              }
            })
          } else if (error instanceof InternalError) {
            formMethods.setError('root', { message: t('core.error.internal_error_message') })
          }
        },
      },
    )
  }

  useEffect(() => {
    if (!opened) {
      formMethods.reset(defaultValues)
      editorRef.current?.clear()
      setDisplaySchedulingOptions(false)
      formMethods.clearErrors()
    }
  }, [opened])

  if (!community || isValidating) {
    return <div className="mb-10 h-28 animate-pulse rounded-lg bg-skeleton-primary" />
  }

  return (
    <>
      <div className="mb-8 flex flex-col items-end bg-background-secondary p-5 lg:rounded-lg">
        <button
          type="button"
          data-testid="add-post-trigger"
          className="h-12 w-full cursor-pointer resize-none overflow-hidden rounded-md border border-text-secondary/30 px-5 text-start text-text-secondary focus:outline-hidden"
          onClick={() => setOpened(true)}
        >
          {t('home.add_post.text_area.placeholder')}
        </button>
      </div>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('settings.create_post.modal.title')}
        data-testid="create-post-modal"
      >
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <AddPostInputField
              name="title"
              maxLength={COMMUNITY_TITLE_MAX_LENGTH}
              label={t('home.add_post.title')}
            />
            <AddPostInputField
              name="path"
              maxLength={32}
              onFocus={convertNameToPath}
              label={t('home.add_post.path')}
            />
            <AddPostTopicSelect />
            <AddPostContentEditor ref={editorRef} />
            <PostAttachments onAddEmoji={handleAddEmoji} />

            {canSchedulePosts && (
              <div data-testid="create-post-scheduling">
                <div className="mb-6 h-0 w-full border-t border-text-secondary/30" />

                <div className="mb-6">
                  <div className="flex items-center gap-x-3">
                    <CheckBoxSwitcher
                      onChange={setDisplaySchedulingOptions}
                      selected={displaySchedulingOptions}
                    />
                    <p className="text-base font-medium text-text-primary">
                      {t('home.add_post.scheduling_post.label')}
                    </p>
                  </div>
                  {displaySchedulingOptions && (
                    <div className="pt-2">
                      <p className="text-base text-text-primary/50">
                        {t('settings.schedule_post.modal.description')}
                      </p>
                      <Controller
                        name="scheduledTo"
                        control={formMethods.control}
                        shouldUnregister
                        defaultValue={null}
                        rules={{
                          required: {
                            value: true,
                            message: t('validation.date_time_required_error'),
                          },
                          validate: value => validateNotPastDate(t, value),
                        }}
                        render={({ field }) => (
                          <DateTimePicker
                            wrapperButtonInputClassName="pt-4"
                            labelClassName="text-base text-text-primary font-medium"
                            label={t('home.add_post.date_time_picker.label')}
                            value={field.value}
                            onChange={date =>
                              field.onChange(date ? new Date(date).toISOString() : null)
                            }
                            timezone={timezone}
                            createdAt={isAuthorizedUser(user) ? user.createdAt : undefined}
                            intlLocale={getLanguageCodeByLocale(
                              user?.dashboardLocale || LocaleEnum.ENGLISH,
                            )}
                            minDate={new Date()}
                          />
                        )}
                      />
                      {scheduledToFormatted && (
                        <p className="mt-2 text-sm text-text-secondary">
                          {t('schedule_post.modal.scheduling_time_zone', {
                            label: scheduledToFormatted.title,
                          })}
                        </p>
                      )}
                      {formMethods.formState.errors.scheduledTo?.message && (
                        <p className="mt-2 text-sm text-danger">
                          {formMethods.formState.errors.scheduledTo.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-x-2">
              <PrimaryButton
                type="submit"
                isLoading={createPostMutation.isPending}
                disabled={isUploading}
              >
                <PlusIcon />
                {t('home.add_post.button.title')}
              </PrimaryButton>
            </div>
            {formMethods.formState.errors.root?.message && (
              <p className="mt-2 text-sm text-danger">
                {formMethods.formState.errors.root.message}
              </p>
            )}
          </form>
        </FormProvider>
      </Modal>
    </>
  )
}

export default AddPost
